import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

interface Office {
  country: string;
  city: string;
  type: string;
  status: 'Operational' | 'Coming Soon';
  coordinates: {
    lng: number;
    lat: number;
  };
  address?: string;
  hours?: string;
}

const TRUELOG_BLUE = '#385CF2';
const TRUELOG_CYAN = '#0E9ED5';
const MAP_GEO_JSON = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const SERVED_FILL = '#0b3a5c';
const SERVED_FILL_HOVER = '#0E9ED5';
const SERVED_STROKE = '#1f6f9c';
const NOT_SERVED = new Set(['Antarctica']);

const reachStats = [
  { value: '140+', label: 'Countries served' },
  { value: '6', label: 'TrueLog offices' },
  { value: '1', label: 'Accountable partner' },
];

const offices: Office[] = [
  {
    country: 'Singapore',
    city: 'Singapore',
    type: 'HQ',
    status: 'Operational',
    coordinates: { lng: 103.8198, lat: 1.3521 },
  },
  {
    country: 'Belgium',
    city: 'Antwerp',
    type: 'Office',
    status: 'Operational',
    coordinates: { lng: 4.4025, lat: 51.2194 },
  },
  {
    country: 'UAE',
    city: 'Dubai',
    type: 'Office',
    status: 'Operational',
    coordinates: { lng: 55.2708, lat: 25.2048 },
  },
  {
    country: 'Vietnam',
    city: 'Ho Chi Minh City',
    type: 'Office',
    status: 'Operational',
    coordinates: { lng: 106.6297, lat: 10.8231 },
  },
  {
    country: 'India',
    city: 'Chennai',
    type: 'Office',
    status: 'Operational',
    coordinates: { lng: 80.2707, lat: 13.0827 },
  },
  {
    country: 'Sri Lanka',
    city: 'Colombo',
    type: 'Office',
    status: 'Operational',
    coordinates: { lng: 79.8612, lat: 6.9271 },
  },
];

const GlobalMap: React.FC = () => {
  const [hoveredOffice, setHoveredOffice] = useState<Office | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  const isSameOffice = (a: Office, b: Office | null) =>
    Boolean(b && a.city === b.city && a.country === b.country);

  return (
    <section className="relative overflow-hidden py-14 lg:py-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-12 h-72 w-72 rounded-full bg-[#385CF2]/20 blur-[110px]" />
        <div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-[#0E9ED5]/20 blur-[130px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:48px_48px] opacity-[0.16]" />
      </div>

      <div className="relative container-custom">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.75 }}
          className="mx-auto mb-10 max-w-3xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-200">
            <MapPinIcon className="h-3.5 w-3.5" />
            Global Coverage
          </span>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white md:text-5xl">
            Our global footprint keeps
            <span className="bg-gradient-to-r from-[#385CF2] to-[#0E9ED5] bg-clip-text text-transparent"> ICT moving, everywhere</span>
          </h2>
          <p className="mt-4 text-base text-slate-300 md:text-lg">
            We service 140+ countries across every continent through one accountable network, anchored by our own
            offices in six locations. Hover the map to explore.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mx-auto mb-8 grid max-w-3xl grid-cols-3 gap-3 sm:gap-4"
        >
          {reachStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl border border-white/15 bg-white/[0.06] px-3 py-4 text-center backdrop-blur-sm sm:px-5"
            >
              <p className="font-display text-3xl font-bold text-white sm:text-4xl">
                <span className="bg-gradient-to-r from-[#8dcaf0] to-[#0E9ED5] bg-clip-text text-transparent">{stat.value}</span>
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-[0.08em] text-slate-300 sm:text-sm">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-white/15 bg-[#081127]/80 p-4 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-6"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-xl font-semibold text-white">Interactive world map</h3>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-3.5 rounded-sm border" style={{ backgroundColor: SERVED_FILL, borderColor: SERVED_STROKE }} />
                Countries we serve (140+)
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TRUELOG_CYAN }} />
                Office
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TRUELOG_BLUE }} />
                HQ
              </span>
            </div>
          </div>

          <div className="relative h-[440px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950 md:h-[560px]">
            <ComposableMap
              projectionConfig={{
                scale: 170,
                center: [8, 12],
              }}
              className="h-full w-full"
              style={{ width: '100%', height: '100%' }}
            >
              <Geographies geography={MAP_GEO_JSON}>
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => {
                    const name: string = geo.properties?.name || '';
                    const served = !NOT_SERVED.has(name);
                    const fill = !served ? '#1e293b' : hoveredCountry === name ? SERVED_FILL_HOVER : SERVED_FILL;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke={served ? SERVED_STROKE : '#334155'}
                        strokeWidth={0.45}
                        onMouseEnter={() => served && setHoveredCountry(name)}
                        onMouseLeave={() => setHoveredCountry(null)}
                        style={{
                          default: { fill, outline: 'none', transition: 'fill 0.25s ease' },
                          hover: { fill: served ? SERVED_FILL_HOVER : '#334155', outline: 'none' },
                          pressed: { fill, outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>

              {offices.map((office, index) => {
                const isActive = isSameOffice(office, hoveredOffice);
                const isHq = office.type === 'HQ';
                const markerColor =
                  office.status === 'Operational'
                    ? isHq
                      ? TRUELOG_BLUE
                      : TRUELOG_CYAN
                    : '#f59e0b';

                return (
                  <Marker
                    key={`${office.city}-${office.country}`}
                    coordinates={[office.coordinates.lng, office.coordinates.lat]}
                    onMouseEnter={() => setHoveredOffice(office)}
                    onMouseLeave={() => setHoveredOffice(null)}
                  >
                    <motion.g
                      className="cursor-pointer"
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: index * 0.04 }}
                      whileHover={{ scale: 1.18 }}
                    >
                      <circle
                        r={isHq ? 9 : 6}
                        fill={markerColor}
                        stroke="#ffffff"
                        strokeWidth={isHq ? 2.8 : 2}
                      />
                      {isHq && <circle r={3.2} fill="#ffffff" />}
                      {isActive && (
                        <circle
                          r={isHq ? 14 : 10}
                          fill="none"
                          stroke={markerColor}
                          strokeWidth={1.4}
                          opacity={0.8}
                          className="animate-ping"
                        />
                      )}
                    </motion.g>
                  </Marker>
                );
              })}
            </ComposableMap>

            <AnimatePresence>
              {(hoveredOffice || hoveredCountry) && (
                <motion.div
                  key={hoveredOffice ? `o-${hoveredOffice.city}` : `c-${hoveredCountry}`}
                  initial={{ opacity: 0, y: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.96 }}
                  className="pointer-events-none absolute left-4 top-4 z-20 rounded-xl border border-white/20 bg-slate-900/85 px-4 py-3 backdrop-blur-lg"
                >
                  {hoveredOffice ? (
                    <>
                      <p className="text-sm font-semibold text-white">
                        {hoveredOffice.city}, {hoveredOffice.country}
                      </p>
                      <p className="text-xs text-slate-300">
                        TrueLog {hoveredOffice.type} · {hoveredOffice.status}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-white">{hoveredCountry}</p>
                      <p className="text-xs text-slate-300">Serviced by the TrueLog network</p>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalMap;
